import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { CollectionView } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellType, DataMap, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem, ModalSize, Rental, TimeUnit, TimeUnitEx, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { RentalComponent } from './rental.component'

@Component({
    templateUrl: './rentals.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class RentalsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch

    activeTab = 0

    grid: Grid<Rental>
    allEquipment: any[] = []
    allJobs: any[] = []
    vendorMap!: DataMap
    jobMap!: DataMap
    allUserMap!: DataMap
    activeUserMap!: DataMap
    maintMap!: DataMap
    billingCycleMap!: DataMap
    rateUnitMap!: DataMap
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsRentalsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<Rental>(this.services)
            .setupModalEditing(this.services.data.rentals, 'Rental', RentalComponent, () => new Rental(), this, ModalSize.large)
            .wrapColumnHeaders()
            .filterBy(item => [item.equipment])

        this.grid.modified.on(this.refreshRentals.bind(this))

        this.refresh()
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshRentals)
    }

    refreshRentals() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.rentals.queryAll()).subscribe({
                next: () => {
                    this.grid.items = this.services.data.rentals.items
                },
            }),
        )
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.rentals.queryAll(),
                    this.services.data.vendors.queryAllByAction('GetForRental'),
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        const view = this.grid.view as CollectionView
                        view._filters.push(x => !x.returnedDate)

                        this.grid.items = this.services.data.rentals.items
                        this.vendorMap = new DataMap(this.services.data.vendors.items, 'id', 'name')
                        const jobMap = this.services.data.vpJobs.items.map(x => new ListItem(x.id, x.id + ' ' + x.name))
                        this.jobMap = new DataMap(jobMap, 'value', 'display')

                        // We want the grid to show a value for any user, even inactive ones.
                        this.allUserMap = new DataMap(result[3] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeUserMap = new DataMap(result[4] as VpUser[], 'id', 'name')

                        const maintMap: ListItem<boolean>[] = [new ListItem(true, 'Rental Company'), new ListItem(false, 'VanCon')]
                        this.maintMap = new DataMap(maintMap, 'value', 'display')
                        const billingCycleMap: ListItem<number>[] = [
                            new ListItem(TimeUnit.Day, TimeUnitEx.toDisplayAdverb(TimeUnit.Day)),
                            new ListItem(TimeUnit.Week, TimeUnitEx.toDisplayWithUnit(TimeUnit.Week)),
                            new ListItem(TimeUnit.TwoWeek, TimeUnitEx.toDisplayWithUnit(TimeUnit.TwoWeek)),
                            new ListItem(TimeUnit.Month, TimeUnitEx.toDisplayWithUnit(TimeUnit.Month)),
                            new ListItem(TimeUnit.OverMonth, TimeUnitEx.toDisplayWithUnit(TimeUnit.OverMonth)),
                        ]
                        this.billingCycleMap = new DataMap(billingCycleMap, 'value', 'display')
                        const rateUnitMap: ListItem<number>[] = [
                            new ListItem(TimeUnit.Hour, TimeUnitEx.toDisplayAdverb(TimeUnit.Hour)),
                            new ListItem(TimeUnit.Day, TimeUnitEx.toDisplayAdverb(TimeUnit.Day)),
                            new ListItem(TimeUnit.Week, TimeUnitEx.toDisplayAdverb(TimeUnit.Week)),
                            new ListItem(TimeUnit.Month, TimeUnitEx.toDisplayAdverb(TimeUnit.Month)),
                        ]
                        this.rateUnitMap = new DataMap(rateUnitMap, 'value', 'display')
                    },
                }),
        )
    }

    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as Rental
            cell.style.textDecoration = 'unset'
            if (dataItem && dataItem.returnedDate != null) {
                cell.style.textDecoration = 'line-through'
            }
        }
    }

    openImage(url) {
        window.open('./Resources/Images/Rental/' + url, '_blank')
    }

    tabChanged() {
        setTimeout(() => {
            const view = this.grid.view as CollectionView

            // Remove all but the first filter
            if (view._filters.length > 1) {
                view._filters.splice(1, 100) // keeps only the first item
            }
            if (this.activeTab === 0) view._filters.push(x => !x.returnedDate)
            else view._filters.push(x => x.returnedDate)
            this.grid.selectedItem = undefined
            this.grid.refresh()
        }, 10)
    }
}
